import { customerAPI } from '../services/api';

let razorpayScript = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  if (!razorpayScript) {
    razorpayScript = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = resolve;
      s.onerror = () => reject(new Error('Failed to load payment gateway'));
      document.body.appendChild(s);
    });
  }
  return razorpayScript;
}

// Shared by every page that offers Online PG checkout (CustomerRenewPage's dedicated
// Renew flow, CustomerPayments' Pay-via-UPI section) — creates the gateway order and
// launches whichever flow the configured provider needs:
//  - Razorpay: JS-modal checkout, calls onSuccess()/onFailure() directly (AJAX, no navigation).
//  - AblePay: hosted-redirect — this navigates the browser away entirely; the customer
//    comes back via the backend's return_url, which redirects to whatever page they
//    started from (window.location.pathname, passed as returnPath) with
//    ?payment=success|failed in the query string for that page to read on mount.
//
// Params: { service, months, planId, packageId (DTD CATV: the package picked on the page), addonPlanIds, purpose, companyName, accentColor, description, onSuccess, onFailure }
// purpose: 'renewal' (default — buys a plan-duration tier via /me/renew/create-order, needs
// `months`; `addonPlanIds` — broadband-only self-service add-ons, see utils/addons.js on the
// backend — are bundled into this same order and attached on success), 'balance' (pays off
// the account's current outstandingBalance server-side, no `months`/`addonPlanIds` needed —
// regular/auto-renew accounts; pass `planId` when there's no balance and no plan assigned
// yet — the customer's picked plan is charged instead, same as the QR+screenshot flow's
// fallback, no auto-assignment), or 'catv-dtd' (CATV's pay-5-get-6/pay-10-get-12 discount
// tiers, computed server-side in /me/catv-dtd/create-order to match the portal's own
// discount display exactly — needs `months`, service is always 'catv').
// onSuccess({ receiptNumber }) / onFailure(message) are only called for Razorpay — AblePay
// never returns here, it's a full navigation.
export async function startOnlinePayment({ service, months, planId, packageId, addonPlanIds, purpose = 'renewal', companyName, accentColor, description, onSuccess, onFailure }) {
  const returnPath = window.location.pathname;
  const { data: orderRes } = purpose === 'balance'
    ? await customerAPI.createBalanceOrder({ service, planId, returnPath })
    : purpose === 'catv-dtd'
      ? await customerAPI.createCatvDtdOrder({ months, packageId, returnPath })
      : await customerAPI.createRenewOrder({ service, months, planId, addonPlanIds, returnPath });
  const order = orderRes.data;

  if (order.provider === 'ablepay') {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = order.actionUrl;
    Object.entries(order.fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    return; // browser is navigating away — nothing left to do here
  }

  await loadRazorpay();
  const rzp = new window.Razorpay({
    key: order.keyId,
    amount: Math.round(order.amount * 100),
    currency: order.currency,
    name: companyName,
    description,
    order_id: order.orderId,
    prefill: order.prefill,
    theme: { color: accentColor },
    handler: async (response) => {
      try {
        const { data: verifyRes } = await customerAPI.verifyRenewPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onSuccess?.({ receiptNumber: verifyRes.data?.receiptNumber });
      } catch (err) {
        onFailure?.(err.response?.data?.message || 'Payment succeeded but confirmation failed — contact support.');
      }
    },
    modal: { ondismiss: () => onFailure?.(null) }, // null = user just closed it, no toast needed
  });
  rzp.on('payment.failed', () => onFailure?.('Payment failed — please try again.'));
  rzp.open();
}
